import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { actions as authActions } from 'features/Auth';
import { actions } from 'features/users/redux';
import parseNumberFromString from 'shared/helpers/parseNumberFromString';

import { amountTypes } from '../data';

const useDepositModel = () => {
  const dispatch = useDispatch();
  const { userId } = useParams();
  const { username: authUsername, balance: authBalance, role: authRole, currency } = useSelector(state => state.auth);
  const history = useHistory();
  const locale = useSelector(state => state.locale.locale);
  const { userInfo, actionProcessing } = useSelector(state => state.users);
  const [amount, setAmount] = useState();
  const [bonusAmount, setBonusAmount] = useState();
  const [bonusPercentAmount, setBonusPercentAmount] = useState();
  const [isBonus, setIsBonus] = useState(false);

  useEffect(() => {
    dispatch(actions.getUserInfo({ id: userId, withParent: true }));
  }, [userId, dispatch]);

  const players = useMemo(
    () => [
      {
        username: userInfo?.parentInfo?.username ?? authUsername,
        balance: userInfo?.parentInfo?.balance ?? authBalance,
        role: userInfo?.parentInfo?.role ?? authRole,
        title: locale.from,
      },
      {
        username: userInfo.username,
        balance: userInfo.balance,
        role: userInfo.role,
        title: locale.to,
      },
    ],
    [userInfo, authUsername, authBalance, authRole, locale],
  );

  const onSubmit = useCallback(() => {
    dispatch(
      actions.depositOrWithdrawal({
        amount,
        operation: 0,
        id: userId,
        bonusAmount,
        bonusPercentAmount,
        isBonus,
        callback: () => {
          dispatch(actions.getUserInfo({ id: userId }));
          dispatch(authActions.getBalance());
          history.push('/users/all');
        },
      }),
    );
  }, [amount, bonusAmount, bonusPercentAmount, dispatch, history, isBonus, userId]);

  const changeValue = useCallback(
    e => {
      const value = e?.currentTarget?.value ?? e;

      if (e?.currentTarget) {
        setAmount(() => parseNumberFromString(value));
      } else {
        setAmount(() => {
          // if (!Object.keys(amountTypes).includes(value)) return Number(value);
          return Number(value) + Number(parseNumberFromString(amount));
        });
      }
    },
    [amount],
  );

  const handleIsBonusChange = useCallback(value => {
    setIsBonus(value);
    if (!value) {
      setBonusPercentAmount('');
      setBonusAmount('');
    }
  }, []);

  const handleBonusAmountChange = useCallback(
    e => {
      const value = e?.currentTarget?.value ?? e;

      if (e?.currentTarget) {
        setBonusAmount(() => value);
      } else {
        setBonusAmount(() => {
          if (!Object.keys(amountTypes).includes(value)) return Number(value);
          return Number(value) + Number(parseNumberFromString(bonusAmount));
        });
      }
      setBonusPercentAmount('');
    },
    [bonusAmount],
  );

  const handleBonusPercentChange = useCallback(e => {
    const value = (e?.currentTarget?.value ?? e).replace('%', '');

    if (e?.currentTarget) {
      setBonusPercentAmount(value);
      setBonusAmount('');
    }
  }, []);

  const handleBonusAllowed = useCallback(v => /^\d{0,2}$/.test(v?.value), []);

  return {
    players,
    amount,
    onSubmit,
    changeValue,
    handleIsBonusChange,
    handleBonusAmountChange,
    handleBonusPercentChange,
    handleBonusAllowed,
    setAmount,
    currency,
    balance: authBalance,
    actionProcessing,
    locale,
    history,
    bonusAmount,
    bonusPercentAmount,
    isBonus,
    targetRole: userInfo.role,
  };
};

export default useDepositModel;
